import { useEffect, useRef, type ReactNode } from 'react';
import './AdminArea.css';

export interface AdminTableProps {
  /** Accessible name of the table (rendered as its caption, also names the scroll region). */
  caption: string;
  /** <thead> + <tbody> content. Header cells use <th scope="col">, row headers <th scope="row">. */
  children: ReactNode;
}

/**
 * Data table of the admin area. The wrapper scrolls horizontally on narrow
 * screens (WCAG 1.4.10 allows two-dimensional scrolling for data tables). While
 * it actually overflows it is made keyboard-focusable (tabindex 0), so keyboard
 * users can scroll it (WCAG 2.1.1) — only then, to avoid a useless tab stop.
 */
export function AdminTable({ caption, children }: AdminTableProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (wrap === null) return undefined;
    const update = (): void => {
      if (wrap.scrollWidth > wrap.clientWidth + 1) wrap.setAttribute('tabindex', '0');
      else wrap.removeAttribute('tabindex');
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    return () => observer.disconnect();
  });

  return (
    <div ref={wrapRef} className="rg-admin-table__wrap" role="region" aria-label={caption}>
      <table className="rg-admin-table">
        <caption>{caption}</caption>
        {children}
      </table>
    </div>
  );
}

export default AdminTable;
